import React, { useEffect, useState } from 'react';
import { REJECTION_COMMENT_MAX_LENGTH, rejectRoute, salespersonLabel, setRouteSalesperson } from '../../services/salesRoutesService';
import { salespersonOptions } from './RouteEditor';
import { reviewSummary } from './RouteParts';
import styles from '../../css/SalesRoutes.module.css';

function DialogShell({ titleId, canClose, onClose, children }) {
    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape' && canClose) onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, canClose]);

    return (
        <div className={styles.dialogBackdrop} onClick={(e) => { if (e.target === e.currentTarget && canClose) onClose(); }}>
            <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
                {children}
            </div>
        </div>
    );
}

export function ChangeSalespersonDialog({ idCompany, route, salespeople, autoAuthorize, onClose, onSaved }) {
    const [salespersonNo, setSalespersonNo] = useState(route.salespersonNo);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            onSaved(await setRouteSalesperson(idCompany, route.id, salespersonNo, { autoAuthorize }));
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <DialogShell titleId="sr-dialog-title" canClose={!saving} onClose={onClose}>
            <h2 id="sr-dialog-title" className={styles.panelTitle}>Change salesperson</h2>
            <p className={styles.dialogRoute}>{route.name}</p>
            <label htmlFor="sr-dialog-salesperson" className={styles.label}>Salesperson</label>
            <select id="sr-dialog-salesperson" autoFocus className={styles.input} value={salespersonNo} onChange={(e) => setSalespersonNo(e.target.value)}>
                {salespersonOptions(salespeople, route.salespersonNo).map((s) => (
                    <option key={s.salespersonNo} value={s.salespersonNo}>{salespersonLabel(s)}</option>
                ))}
            </select>
            {!autoAuthorize && <p className={styles.dialogHint}>This change sends the route back to Pending to review.</p>}
            {error && <p className={styles.fieldError} role="alert">{error}</p>}
            <div className={styles.dialogActions}>
                <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving || salespersonNo === route.salespersonNo}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </DialogShell>
    );
}

export function RejectRouteDialog({ idCompany, route, onClose, onSaved }) {
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            onSaved(await rejectRoute(idCompany, route.id, comment));
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <DialogShell titleId="sr-reject-title" canClose={!saving} onClose={onClose}>
            <h2 id="sr-reject-title" className={styles.panelTitle}>Reject route</h2>
            <p className={styles.dialogRoute}>{route.name}</p>
            <label htmlFor="sr-reject-comment" className={styles.label}>Comment (optional)</label>
            <textarea
                id="sr-reject-comment"
                autoFocus
                className={styles.textarea}
                value={comment}
                maxLength={REJECTION_COMMENT_MAX_LENGTH}
                onChange={(e) => setComment(e.target.value)}
            />
            <p className={styles.charCount}>{comment.length} / {REJECTION_COMMENT_MAX_LENGTH}</p>
            {error && <p className={styles.fieldError} role="alert">{error}</p>}
            <div className={styles.dialogActions}>
                <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className={styles.btnReject} onClick={save} disabled={saving}>
                    {saving ? 'Rejecting…' : 'Reject route'}
                </button>
            </div>
        </DialogShell>
    );
}

export function RejectionCommentDialog({ route, onClose }) {
    return (
        <DialogShell titleId="sr-comment-title" canClose onClose={onClose}>
            <h2 id="sr-comment-title" className={styles.panelTitle}>Rejection comment</h2>
            <p className={styles.dialogRoute}>{route.name}</p>
            {route.rejectionComment
                ? <p className={styles.reviewComment}>{route.rejectionComment}</p>
                : <p className={styles.emptyInline}>No comment was left.</p>}
            <p className={styles.dialogHint}>{reviewSummary(route)}</p>
            <div className={styles.dialogActions}>
                <button type="button" autoFocus className={styles.btnPrimary} onClick={onClose}>Close</button>
            </div>
        </DialogShell>
    );
}
